<template>
  <div class="terms">
    <div class="header mt-3 h1">
      AML/CTF and Sanctions Measures Statement
    </div>
    <div class="header mt-2 h1">
      High-level overview of measures and controls
    </div>
    <div class="header mt-2 h1">
      RWA.capital
    </div>
    <div class="h5 mt-5 ms-4 me-4">
      <p><strong>Effective date:</strong> February, 12 2026</p>
      <p><strong>Public version</strong> for external users (for website)</p>

      <h3 class="mt-4">
        1. Purpose
      </h3>
      <p>
        RWA.capital is committed to preventing the use of our platform, products, and services for money laundering, terrorist financing, the financing of the proliferation of weapons of mass destruction (WMD), or the evasion of sanctions.
        This statement provides a high-level public summary of the key measures we employ to satisfy the requirements of the applicable AML/CFT and sanctions regime within the DIFC.
      </p>

      <h3 class="mt-4">
        2. Scope
      </h3>
      <p>
        This statement applies to investor onboarding, subscriptions/investments, distributions, redemptions/exit windows, and related payment flows and counterparties within our real estate investment offerings (including investments via SPVs and similar structures).
      </p>
      <p>
        Where we use third-party providers (e.g., for identification, sanctions screening, payment infrastructure or monitoring tools), we shall exercise appropriate controls and oversight in accordance with a risk-based approach.
      </p>

      <h3 class="mt-4">
        3. Regulatory Framework (Overview)
      </h3>
      <p>
        Our AML/CTF and sanctions compliance measures are designed with regard to DFSA requirements (AML Module: Anti-Money Laundering, Counter-Terrorist Financing and Sanctions) and applicable UAE federal AML/CTF legislation, to the extent it applies in the DIFC. This includes obligations relating to customer due diligence (CDD), ongoing monitoring, record-keeping, and reporting of any suspicious transactions and activity to the UAE FIU, with subsequent notification to the DFSA where required.
      </p>
      <p>
        <strong>Note:</strong> this summary is offered for awareness purposes only and shall not constitute legal advice.
      </p>

      <h3 class="mt-4">
        4. Governance and Accountability
      </h3>
      <p>
        RWA.capital maintains an AML/CTF and sanctions governance framework with clear allocation of responsibilities and escalation channels.
      </p>
      <p>
        Contact: for any AML/CTF and sanctions queries, please contact Compliance/MLRO at <a href="mailto: policy@rwa.capital">policy@rwa.capital</a>
      </p>

      <h3 class="mt-4">
        5. Risk-Based Approach
      </h3>
      <p>
        We apply a risk-based approach to identify, assess and manage AML/CTF and sanction risks. Our assessment considers, among other factors:
      </p>
      <ul>
        <li>Customer risk (residency/citizenship, occupation, source of funds/wealth, PEP status).</li>
        <li>Geographic risk (higher-risk and/or sanctioned jurisdictions).</li>
        <li>Product/service risk (investment structures, payment methods, secondary/exit mechanisms).</li>
        <li>Delivery channel risk (fully remote/digital onboarding).</li>
        <li>Transaction behavioral risk (unusual patterns, rapid in-and-out flows, third-party funding).</li>
      </ul>

      <h3 class="mt-4">
        6. CDD and Onboarding Checks
      </h3>
      <p>
        Registration on the platform may be completed before KYC/AML procedures are completed.
        However, any functions involving the acceptance, transfer or return of funds (including investing, top-ups, withdrawals, distributions/payouts) shall be enabled only after completion of CDD/KYC and sanctions/PEP screening and, where necessary, internal approval (including by the MLRO).
      </p>
      <ul>
        <li>Preliminary sanctions and PEP screening shall be performed at registration and/or prior to activation of payment functionality.</li>
        <li>Full identification and verification of the customer (and, where required, UBOs/controlling persons) shall be completed prior to processing the first investment or any other funds flow.</li>
        <li>Customer data shall be refreshed in line with the risk level and upon triggers (substantial profile changes, unusual activity, new sanctions matches, etc.).</li>
      </ul>
      <p>
        For identity and document verification and/or sanctions screening we may engage a specialized KYC/identity provider and/or sanctions screening services.
        Where activities are outsourced, we shall carry out due diligence on the provider, set requirements for quality and availability of evidence, and exercise ongoing oversight; responsibility for AML/CTF and sanctions compliance remains with RWA.capital.
      </p>
      <p>
        CDD shall be performed before establishing a business relationship for the purposes of financial services and prior to processing transactions/investments; limited registration before completion of CDD shall be permitted provided that no funds-related activity occurs.
      </p>

      <h3 class="mt-4">
        6.1 Individuals
      </h3>
      <ul>
        <li>Identification and identity verification (passport/national ID).</li>
        <li>Selfie/liveness and facial matching (for remote onboarding).</li>
        <li>Proof of address (utility bill/bank statement/tenancy contract, etc.).</li>
        <li>Collection of information regarding residency, occupation, purpose and intended nature of the relationship.</li>
        <li>Collection/assessment of source of funds (SoF) and, where required, source of wealth (SoW).</li>
      </ul>

      <h3 class="mt-4">
        6.2 Legal Entities and SPV-Related Parties
      </h3>
      <ul>
        <li>Incorporation and licensing documents (extract/certificate, license, memorandum & articles, etc.).</li>
        <li>Verification of signatory and governance authorities.</li>
        <li>Establishing ownership structure and UBOs/controlling persons, including identification and screening.</li>
        <li>Understanding the nature of business, sources of funds and purpose of the relationship.</li>
      </ul>

      <h3 class="mt-4">
        6.3 Additional Information and Requests
      </h3>
      <p>
        We may request additional documents/explanations, including confirmation of a bank account in the customer’s name, information on third parties, evidence of source of funds, and may conduct periodic data refresh.
      </p>

      <h3 class="mt-4">
        7. Enhanced Due Diligence (EDD)
      </h3>
      <p>
        Enhanced measures (EDD) are applied to higher-risk customers and activities, including additional supporting documentation, deeper review and senior management/MLRO approval (e.g., for PEPs, complex structures, higher-risk jurisdictions).
      </p>

      <h3 class="mt-4">
        8. Sanctions Compliance
      </h3>
      <p>
        We conduct sanctions screening of customers, UBOs and relevant parties against applicable sanctions lists and registers. Potential matches (hits) shall be reviewed and escalated; should a match be confirmed, blocking/freezing measures and notifications shall be applied subject to applicable requirements.
      </p>

      <h3 class="mt-4">
        9. Ongoing Monitoring
      </h3>
      <p>
        We perform ongoing monitoring of relationships and transactions to identify unusual/suspicious indicators, consistency with the customer’s expected profile, and continued adequacy of documents and information.
      </p>

      <h3 class="mt-4">
        10. Suspicious Activity Reporting (STR/SAR)
      </h3>
      <p>
        Employees are required to promptly report suspicious indicators to the MLRO/Compliance. The decision to file an STR/SAR shall be taken by the MLRO. Reports shall be filed with the UAE FIU and, where required, accompanied by notification to the DFSA. “Tipping-off” (disclosing the fact of filing/planning a report) is prohibited.
      </p>

      <h3 class="mt-4">
        11. Record Keeping
      </h3>
      <p>
        We shall retain CDD, screening and transaction records for at least the minimum periods required by applicable rules (typically at least 6 years after the end of the relationship), while ensuring confidentiality and data protection.
      </p>

      <h3 class="mt-4">
        12. Training
      </h3>
      <p>
        Employees shall receive regular AML/CTF and sanctions training, including identification of red flags, escalation procedures and confidentiality requirements.
      </p>

      <h3 class="mt-4">
        13. Review
      </h3>
      <p>
        This statement and related policies shall be reviewed at least annually and/or upon changes in laws, the business model, or identified risks.
      </p>
    </div>
  </div>
</template>

<script setup>
import { checkAccess, scope } from "@/help/accessData";
import { onMounted } from "vue";
import { useRouter } from "vue-router";

const router = useRouter();

onMounted(() => {
  if (checkAccess(scope.notRwa)) {
    router.push({ name: "page not found" });
  }
});
</script>

<style scoped lang="scss">
ul {
  list-style-type: disc;
}
.terms {
  background-color: var(--main-color);
  padding-bottom: 10px;

  .header {
    display: flex;
    justify-content: center;
  }
}
a {
  color: var(--color-green);
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}
</style>